import { type UseFormInput, useForm } from '@mantine/form';
import { getLogger } from 'loglevel';
import { useCallback, useState } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { ServerError, ServerValidationError } from '@apple/utils/api/errors';
import type { ErrorMessage, ErrorMessages, FormErrors } from '@apple/utils/validation/models';

const log = getLogger('validation');

export type UseValidatedFormProps<
	Values extends Record<string, unknown> = Record<string, unknown>,
	TransformedValues = Values,
> = UseFormInput<Values, TransformedValues> & {
	/** A unique name for the form */
	name: string;
	onValidSubmit: (data: TransformedValues) => Promise<void>;
	onInvalidSubmit?: () => void;
	onAdditionalValidation?: (data: Values) => ErrorMessage | ErrorMessages | null | undefined;
	fallbackErrorMessage?: string;
	ignoreModelErrorParent?: boolean;
};

export function useValidatedForm<
	Values extends Record<string, unknown> = Record<string, unknown>,
	TransformedValues = Values,
>({
	name,
	initialValues,
	onValidSubmit,
	onInvalidSubmit,
	onAdditionalValidation,
	fallbackErrorMessage,
	validate,
	ignoreModelErrorParent = false,
	...useFormProps
}: UseValidatedFormProps<Values, TransformedValues>) {
	const { t } = useTranslation('common');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [formErrors, setFormErrors] = useState<FormErrors>();

	const form = useForm<Values, TransformedValues>({
		validateInputOnChange: true,
		validateInputOnBlur: true,

		...useFormProps,
		name,
		initialValues,
		validate,
	});

	const translateServerValidationMessage = useCallback(
		(message: ErrorMessage | ErrorMessages) => {
			const translate = (value: string) => t(`zod:errors.${value}`, { defaultValue: value });

			return Array.isArray(message) ? message.map(translate) : translate(message);
		},
		[t],
	);

	const transformServerValidationErrorField = useCallback(
		(field: string) => {
			let fieldPath = field.split('.');
			if (fieldPath.length <= 1) {
				return field;
			}

			if (ignoreModelErrorParent) {
				fieldPath = fieldPath.slice(1);
			}

			// Nested fields are not camel-cased in model validation error response
			for (let i = 0; i < fieldPath.length; i++) {
				const value = fieldPath[i];
				if (value === undefined) {
					continue;
				}
				fieldPath[i] = value.charAt(0).toLowerCase() + value.slice(1);
			}

			// Pathing from the backend looks like an array indexer:
			//  list[0].field
			// in Mantine form it uses dots:
			//  list.0.field
			return fieldPath.join('.').replace(']', '').replace('[', '.');
		},
		[ignoreModelErrorParent],
	);

	const handleValidationError = useCallback(
		(error: ServerValidationError<Values>) => {
			if (error.problem.formErrors) {
				log.warn('Form level errors:', error.problem.formErrors?.length ?? 0);
				setFormErrors(error.problem.formErrors);
			}

			if (error.problem.fieldErrors) {
				const fieldErrors = Object.entries<ErrorMessage | ErrorMessages>(
					error.problem.fieldErrors,
				);

				log.warn('Field level errors:', fieldErrors.length);
				fieldErrors.forEach(([field, error]) => {
					form.setFieldError(
						transformServerValidationErrorField(field),
						translateServerValidationMessage(error),
					);
				});
			}
		},
		[form, transformServerValidationErrorField, translateServerValidationMessage],
	);

	const handleError = useCallback((error: Error) => {
		log.warn('Server error:', error.message);
		setFormErrors([error.message]);
	}, []);

	const handleSubmit = useCallback(
		async (data: TransformedValues) => {
			log.info(`Handling '${name}' form submit:`, data);
			setIsSubmitting(true);
			setHasSubmitted(true);

			try {
				log.info(`Validating '${name}' form:`, data);

				if (onAdditionalValidation) {
					const result = onAdditionalValidation(form.values);

					if (result && result.length > 0) {
						log.error('Additional form validation failed:', result);
						setFormErrors(typeof result === 'string' ? [result] : result);
						onInvalidSubmit?.();
						return;
					}
				}

				const validateResult = form.validate();
				if (validateResult.hasErrors) {
					log.warn('Form validation failed:', data, validateResult);
					return;
				}

				log.info(`Submitting '${name}' form:`, data);
				await onValidSubmit(data);
				setFormErrors(undefined);
			} catch (error) {
				log.warn('An error occurred while attempting to submit form:\n', error);

				if (error instanceof ServerValidationError) {
					log.warn('Validation error:', error.message);
					handleValidationError(error as ServerValidationError<Values>);
				} else if (error instanceof ServerError) {
					log.error('Server error:', error.message);
					handleError(error);
				} else {
					log.error('Unknown error:', error);
					handleError(new Error(fallbackErrorMessage ?? t('common:error.generic')));
				}

				if (onInvalidSubmit) {
					log.info(`Handling '${name}' form invalid submit`);
					onInvalidSubmit();
				}
			} finally {
				setIsSubmitting(false);
			}
		},
		[
			fallbackErrorMessage,
			form,
			handleError,
			handleValidationError,
			name,
			onAdditionalValidation,
			onInvalidSubmit,
			onValidSubmit,
			t,
		],
	);

	return {
		form,
		isSubmitting,
		hasSubmitted,
		formErrors,
		handleSubmit,
	};
}
